import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, MessageCircle, Maximize2, Search, SquarePen, X,
} from 'lucide-react';
import { apiFetch, useAuth } from '../../../context/AuthContext';
import { useMiniChat } from '../context/MiniChatContext';
import ConversationAvatar from './ConversationAvatar';
import ChatWindow from './ChatWindow';
import { getAvatarColor, toOpaqueColor } from '../utils/formatters';
import { isSameUser, getFullInboxPath } from '../utils/userHelpers';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nexploy-eg.com';

// Public/unauthenticated routes — the dock has nothing to show before login
// and shouldn't render on top of them.
const HIDDEN_ON_ROUTES = ['/login', '/reset-password'];

// "Mohamed Ahmed Kamal Zaki" -> "Mohamed Ahmed" — matches the truncation
// already used for notification sender names elsewhere in the app.
const firstTwoWords = (name) => {
  if (!name) return '';
  return name.trim().split(/\s+/).slice(0, 2).join(' ');
};

// Group rows: concatenate member names and truncate — "Mohamed A. and Moha…"
const groupRowLabel = (conv) => {
  if (!conv.isGroup) return firstTwoWords(conv.englishName || conv.name);
  const full = conv.name || conv.englishName || '';
  return full.length > 28 ? `${full.slice(0, 27).trimEnd()}…` : full;
};

const MiniChatDock = () => {
  const {
    conversations, setConversations, openWindows, openChat, closeChat, typingUsers, onlineUsers,
    employeeProfilePics, allEmployees, c, isRTL, t, isMessengerPageOpen, miniChatDisabled,
  } = useMiniChat();

  const { hasFeatureAccess } = useAuth();

  // ── Admin-level feature gating (registry page "messages") ────────────────
  // Same idiom as MessengerPage: only constrains promoted users in Admin
  // View; user/manager views untouched. hasFeatureAccess bypasses for
  // admin/superadmin roles.
  const dockViewType =
    localStorage.getItem('managerViewType') ||
    localStorage.getItem('viewType') ||
    'user';
  const isAdminViewGated = dockViewType === 'admin';
  const canViewMessages = !isAdminViewGated || hasFeatureAccess('messages', 'view');
  const canSendMessages = !isAdminViewGated || hasFeatureAccess('messages', 'send');
  const canCreateGroup = !isAdminViewGated || hasFeatureAccess('messages', 'createGroup');

  // 'collapsed' | 'panel' | 'newMessage'
  const [dockState, setDockState] = useState('collapsed');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]); // multi-select for group start
  const [creatingGroup, setCreatingGroup] = useState(false);
  const searchRef = useRef(null);
  const containerRef = useRef(null);

  // The dock is a floating widget meant for desktop multitasking — on mobile
  // there's no room for it alongside the app's own bottom nav / topbar, and
  // it just sits on top of page content. Hide it entirely below the same
  // mobile breakpoint used everywhere else in the messenger.
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const location = useLocation();
  const navigate = useNavigate();

  const hasActiveWindow = openWindows.some((w) => !w.minimized);
  // A chat window opening takes over — collapse the panel/compose back down.
  const panelVisible = (dockState === 'panel' || dockState === 'newMessage') && !hasActiveWindow;

  const resetToCollapsed = () => {
    setDockState('collapsed');
    setSearch('');
    setSelectedIds([]);
  };

  // Clicking anywhere outside the widget (panel, launcher, or an open chat
  // window) collapses everything back down to just the launcher pill.
  useEffect(() => {
    if (!panelVisible && openWindows.length === 0) return;
    const onPointerDown = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      // Media viewer / forward / delete-confirm / message-actions menus are
      // React-portaled to document.body, so they're never a DOM descendant
      // of the widget above — without this check, clicking inside any of
      // them (e.g. confirming a delete) would look like an outside click
      // and slam the whole widget shut mid-action.
      if (e.target.closest?.('[data-modal-root]')) return;
      resetToCollapsed();
      openWindows.forEach((w) => closeChat(w.chatId));
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelVisible, openWindows, closeChat]);

  // The app's own messenger design tokens (messengerColors.js), already used
  // throughout every other messenger surface — `popoverBg`/`popoverBorder`
  // are the system's existing floating-surface pair, so the widget matches
  // the rest of the app in both light and dark instead of inventing its own
  // palette. `popoverBg` itself is only 95% opaque (by design, for the full
  // page's glass effect) — `toOpaqueColor` strips that alpha so this small
  // popup, sitting over arbitrary page content, is never see-through.
  const surfaces = {
    popupBg: toOpaqueColor(c.popoverBg),
    inputBg: toOpaqueColor(c.inputBg),
    border: c.popoverBorder,
    text: c.text,
    muted: c.textSecondary,
    hover: c.hoverBg,
    accent: c.myMessageBg,
    unreadBadge: '#2563eb', // matches the shared <UnreadBadge/> component's default
  };

  const isConvOnline = (id) => Array.from(onlineUsers || []).some((oid) => isSameUser(oid, id));

  const visibleConversations = useMemo(() => (
    [...conversations]
      .filter((conv) => !conv.isArchived)
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
  ), [conversations]);

  const totalUnread = visibleConversations.reduce((sum, conv) => sum + (conv.unread || 0), 0);
  const recentAvatars = visibleConversations.slice(0, 3);

  const query = search.trim().toLowerCase();
  const filteredConversations = query
    ? visibleConversations.filter((conv) =>
        `${conv.name || ''} ${conv.englishName || ''}`.toLowerCase().includes(query))
    : visibleConversations;

  // "New message" picker — the full org's visibility-scoped employee list,
  // same source the full Messenger page's compose flow uses.
  const filteredEmployees = useMemo(() => {
    const list = allEmployees || [];
    if (!query) return list;
    return list.filter((emp) =>
      `${emp.name || ''} ${emp.englishName || ''}`.toLowerCase().includes(query));
  }, [allEmployees, query]);

  const toggleSelected = (employee) => {
    setSelectedIds((prev) => {
      const id = String(employee.id);
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      // Without the "create group" permission, dock composing stays 1:1 —
      // picking a new person replaces rather than adds to the selection.
      if (!canCreateGroup) return [id];
      return [...prev, id];
    });
  };

  const openExistingOrNewChat = (employee) => {
    const empId = String(employee.id);
    const existing = conversations.find((conv) => String(conv.id) === empId);
    if (existing) {
      openChat(existing);
      return;
    }
    const newConv = {
      id: empId,
      name: employee.name,
      englishName: employee.englishName,
      arabicName: employee.arabicName,
      avatar: employee.avatar,
      company: employee.company,
      department: employee.department,
      position: employee.position,
      email: employee.email,
      lastMessage: '',
      time: '',
      timestamp: Date.now(),
      unread: 0,
      online: isConvOnline(empId),
    };
    setConversations((prev) => [newConv, ...prev]);
    openChat(newConv);
  };

  // Single selection -> open/start that 1:1 chat directly. Two or more ->
  // create a named group (backend requires a name + 2+ members), naming it
  // the same way existing group rows are labelled (concatenated, truncated).
  const startChat = async () => {
    if (selectedIds.length === 0) return;
    if (selectedIds.length === 1) {
      const employee = filteredEmployees.find((e) => String(e.id) === selectedIds[0])
        || (allEmployees || []).find((e) => String(e.id) === selectedIds[0]);
      if (employee) openExistingOrNewChat(employee);
      resetToCollapsed();
      return;
    }

    const members = selectedIds
      .map((id) => (allEmployees || []).find((e) => String(e.id) === id))
      .filter(Boolean);
    if (members.length < 2) return;

    const autoName = members.map((m) => firstTwoWords(m.englishName || m.name)).join(', ');
    setCreatingGroup(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/chat-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: autoName, members: members.map((m) => m.id) }),
      });
      if (!response.ok) throw new Error('Failed to create group');
      const created = await response.json();
      const groupId = `group-${created.id}`;
      const group = {
        id: groupId,
        name: created.name,
        englishName: created.name,
        avatar: created.name.trim().split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase(),
        isGroup: true,
        lastMessage: '',
        time: t.justNow,
        timestamp: Date.now(),
        unread: 0,
        members: created.members || [],
      };
      setConversations((prev) => (prev.some((c2) => c2.id === groupId) ? prev : [group, ...prev]));
      openChat(group);
      resetToCollapsed();
    } catch (err) {
      console.error('Failed to create group chat:', err);
    } finally {
      setCreatingGroup(false);
    }
  };

  // "You: " prefix for 1:1 previews when the last message was ours — not
  // reliable for groups (backend doesn't return a last-sender id for those
  // yet), so groups just show the plain preview text.
  const rowPreview = (conv, typing) => {
    if (typing) return t.typing;
    if (!conv.lastMessage) return conv.online ? (isRTL ? 'نشط الآن' : 'Active now') : '';
    const prefix = !conv.isGroup && conv.isFromMe ? (isRTL ? 'أنت: ' : 'You: ') : '';
    return `${prefix}${conv.lastMessage}`;
  };

  const goToFullInbox = () => {
    navigate(getFullInboxPath());
    resetToCollapsed();
  };

  // Admin-view users without the messages "view" feature get no dock at all.
  if (!canViewMessages) return null;
  // User turned it off from Settings.
  if (miniChatDisabled) return null;
  // No room for a floating dock on mobile — hide it there entirely.
  if (isMobile) return null;
  // The full inbox is its own, complete chat surface — don't show a second,
  // redundant one on top of it.
  if (isMessengerPageOpen) return null;
  if (HIDDEN_ON_ROUTES.includes(location.pathname)) return null;
  if (visibleConversations.length === 0) return null;

  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  // ── State A: collapsed pill ────────────────────────────────────────────
  const collapsedPill = (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        background: surfaces.popupBg, border: `1px solid ${surfaces.border}`,
        borderRadius: '999px', padding: '8px 10px',
        boxShadow: c.shadow,
      }}
    >
      <button
        type="button"
        onClick={() => setDockState('panel')}
        title={isRTL ? 'الرسائل' : 'Messages'}
        aria-label={isRTL ? 'الرسائل' : 'Messages'}
        style={{
          position: 'relative', display: 'flex', alignItems: 'center', gap: '6px',
          border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px',
        }}
      >
        <MessageCircle size={17} color={surfaces.text} />
        <span style={{ fontSize: '13.5px', fontWeight: 700, color: surfaces.text }}>
          {t.messages}
        </span>
        {totalUnread > 0 && (
          <span
            style={{
              minWidth: '16px', height: '16px', background: surfaces.unreadBadge, color: '#fff',
              fontSize: '10px', fontWeight: 700, borderRadius: '8px',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 4px', boxSizing: 'border-box',
            }}
          >
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {recentAvatars.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {recentAvatars.map((conv, idx) => (
            <div
              key={conv.id}
              title={conv.englishName || conv.name}
              onClick={() => openChat(conv)}
              style={{
                cursor: 'pointer',
                marginInlineStart: idx === 0 ? 0 : '-10px',
                zIndex: recentAvatars.length - idx,
                borderRadius: '50%',
                border: `2px solid ${surfaces.popupBg}`,
              }}
            >
              <ConversationAvatar
                conv={conv}
                size={30}
                employeeProfilePics={employeeProfilePics}
                getAvatarColor={getAvatarColor}
                isOnline={isConvOnline(conv.id)}
                unreadCount={0}
                c={{ ...c, sidebarBg: surfaces.popupBg }}
                active={false}
              />
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setDockState('panel')}
        title={isRTL ? 'عرض المزيد' : 'Show more'}
        aria-label={isRTL ? 'عرض المزيد' : 'Show more'}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '28px', height: '28px', border: 'none', background: surfaces.hover,
          color: surfaces.text, cursor: 'pointer', borderRadius: '50%', fontWeight: 700,
          fontSize: '14px', letterSpacing: '1px',
        }}
      >
        ···
      </button>
    </div>
  );

  // ── State B / C: expanded panel ────────────────────────────────────────
  const panel = panelVisible && (
    <div
      style={{
        width: '360px',
        maxWidth: 'calc(100vw - 32px)',
        height: 'min(500px, 72vh)',
        display: 'flex',
        flexDirection: 'column',
        background: surfaces.popupBg,
        border: `1px solid ${surfaces.border}`,
        borderRadius: '18px',
        boxShadow: c.shadow,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px 10px', flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          {dockState === 'newMessage' ? (
            <>
              <button
                type="button"
                onClick={() => { setDockState('panel'); setSearch(''); setSelectedIds([]); }}
                aria-label={isRTL ? 'رجوع' : 'Back'}
                style={{
                  border: 'none', background: 'transparent', color: surfaces.text,
                  cursor: 'pointer', display: 'flex', padding: '4px', marginInlineStart: '-4px',
                }}
              >
                <BackIcon size={18} />
              </button>
              <span style={{ fontSize: '16px', fontWeight: 800, color: surfaces.text }}>
                {t.newMessages}
              </span>
            </>
          ) : (
            <>
              <span style={{ fontSize: '16px', fontWeight: 800, color: surfaces.text }}>
                {t.messages}
              </span>
              {totalUnread > 0 && (
                <span
                  style={{
                    minWidth: '18px', height: '18px', background: surfaces.unreadBadge, color: '#fff',
                    fontSize: '10px', fontWeight: 700, borderRadius: '9px',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 5px', boxSizing: 'border-box',
                  }}
                >
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
          {dockState === 'panel' && (
            <button
              type="button"
              onClick={goToFullInbox}
              title={isRTL ? 'فتح صفحة الرسائل' : 'Open full inbox'}
              aria-label={isRTL ? 'فتح صفحة الرسائل' : 'Open full inbox'}
              style={{
                border: 'none', background: 'transparent', color: surfaces.text,
                cursor: 'pointer', display: 'flex', padding: '6px', borderRadius: '8px',
              }}
            >
              <Maximize2 size={16} />
            </button>
          )}
          <button
            type="button"
            onClick={resetToCollapsed}
            title={t.miniChatCollapse}
            aria-label={t.miniChatCollapse}
            style={{
              border: 'none', background: 'transparent', color: surfaces.text,
              cursor: 'pointer', display: 'flex', padding: '6px', borderRadius: '8px',
            }}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Search — filters conversations, or "To:" people search when composing */}
      <div style={{ padding: '0 16px 10px', flexShrink: 0 }}>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: surfaces.inputBg, borderRadius: '10px',
            padding: '7px 12px',
          }}
        >
          {dockState === 'newMessage' && (
            <span style={{ fontSize: '13px', fontWeight: 600, color: surfaces.muted, flexShrink: 0 }}>
              {isRTL ? 'إلى:' : 'To:'}
            </span>
          )}
          <Search size={15} color={surfaces.muted} style={{ flexShrink: 0 }} />
          <input
            ref={searchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.searchPeople}
            aria-label={t.searchPeople}
            style={{
              flex: 1, minWidth: 0, border: 'none', outline: 'none',
              background: 'transparent', color: surfaces.text,
              fontSize: '13px',
            }}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label={t.miniChatClose}
              style={{ border: 'none', background: 'transparent', color: surfaces.muted, cursor: 'pointer', display: 'flex', padding: 0 }}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Rows — spacing only, no divider lines */}
      <div
        className="mini-chat-scroll"
        style={{
          flex: 1, overflowY: 'auto', padding: '0 8px',
          paddingBottom: dockState === 'newMessage' ? '8px' : '72px',
        }}
      >
        {dockState === 'newMessage' ? (
          filteredEmployees.length === 0 ? (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: surfaces.muted, fontSize: '13px' }}>
              {t.tryDifferent}
            </div>
          ) : (
            filteredEmployees.map((emp) => {
              const label = firstTwoWords(emp.englishName || emp.name);
              const checked = selectedIds.includes(String(emp.id));
              return (
                <button
                  key={emp.id}
                  type="button"
                  onClick={() => toggleSelected(emp)}
                  aria-label={emp.englishName || emp.name}
                  aria-pressed={checked}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    width: '100%', textAlign: 'start',
                    border: 'none', borderRadius: '12px',
                    background: 'transparent',
                    padding: '8px 10px', cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = surfaces.hover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <ConversationAvatar
                    conv={emp}
                    size={44}
                    employeeProfilePics={employeeProfilePics}
                    getAvatarColor={getAvatarColor}
                    isOnline={isConvOnline(emp.id)}
                    unreadCount={0}
                    c={{ ...c, sidebarBg: surfaces.popupBg }}
                    active={false}
                  />
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span
                      style={{
                        fontSize: '13.5px', fontWeight: 600, color: surfaces.text,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </span>
                    {(emp.department || emp.position) ? (
                      <span
                        style={{
                          fontSize: '12px', color: surfaces.muted,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {emp.department || emp.position}
                      </span>
                    ) : null}
                  </div>
                  <span
                    style={{
                      width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                      border: `2px solid ${checked ? surfaces.accent : surfaces.border}`,
                      background: checked ? surfaces.accent : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {checked && <Check size={12} color="#fff" strokeWidth={3} />}
                  </span>
                </button>
              );
            })
          )
        ) : filteredConversations.length === 0 ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: surfaces.muted, fontSize: '13px' }}>
            {t.tryDifferent}
          </div>
        ) : (
          filteredConversations.map((conv) => {
            const isOpen = openWindows.some((w) => String(w.chatId) === String(conv.id));
            const typing = conv.isGroup
              ? false // group typing isn't implemented anywhere in the app yet
              : typingUsers.has(String(conv.id));
            const label = groupRowLabel(conv);
            const hasUnread = (conv.unread || 0) > 0;
            const preview = rowPreview(conv, typing);

            return (
              <button
                key={conv.id}
                type="button"
                // openChat already handles the "already open" case (restores +
                // refreshes it in place) — always routing through it here means
                // clicking an already-open (even non-minimized) conversation
                // still brings it to front / focuses it, instead of doing
                // nothing when `isOpen` was true.
                onClick={() => openChat(conv)}
                aria-label={conv.englishName || conv.name}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  width: '100%', textAlign: 'start',
                  border: 'none', borderRadius: '12px',
                  background: isOpen ? surfaces.hover : 'transparent',
                  padding: '8px 10px', cursor: 'pointer',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = surfaces.hover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = isOpen ? surfaces.hover : 'transparent'; }}
              >
                {/* Avatar + green online dot only — the numeric badge lives
                    on the launcher, and unread is signalled by the blue dot */}
                <ConversationAvatar
                  conv={conv}
                  size={52}
                  employeeProfilePics={employeeProfilePics}
                  getAvatarColor={getAvatarColor}
                  isOnline={isConvOnline(conv.id)}
                  unreadCount={0}
                  c={{ ...c, sidebarBg: surfaces.popupBg }}
                  active={false}
                />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span
                    style={{
                      fontSize: '13.5px',
                      fontWeight: hasUnread ? 700 : 500,
                      color: surfaces.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    {preview ? (
                      <span
                        style={{
                          flex: 1, minWidth: 0,
                          fontSize: '12px',
                          fontWeight: hasUnread ? 600 : 400,
                          color: typing ? '#2563eb' : (hasUnread ? surfaces.text : surfaces.muted),
                          fontStyle: typing ? 'italic' : 'normal',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {preview}
                      </span>
                    ) : <span />}
                    {conv.time ? (
                      <span style={{ fontSize: '11px', color: surfaces.muted, flexShrink: 0 }}>
                        {conv.time}
                      </span>
                    ) : null}
                  </div>
                </div>
                {/* Unread marker: small solid blue dot at the far end of the row */}
                {hasUnread && (
                  <span
                    style={{
                      width: '8px', height: '8px', borderRadius: '50%',
                      background: surfaces.accent, flexShrink: 0,
                    }}
                  />
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Footer — floating compose button (panel) / full-width Chat button (new message) */}
      {dockState === 'panel' ? (
        canSendMessages && (
        <button
          type="button"
          onClick={() => { setDockState('newMessage'); setSearch(''); setSelectedIds([]); requestAnimationFrame(() => searchRef.current?.focus()); }}
          title={t.newMessages}
          aria-label={t.newMessages}
          style={{
            position: 'absolute', bottom: '16px', [isRTL ? 'left' : 'right']: '16px',
            width: '44px', height: '44px', borderRadius: '50%',
            border: 'none', background: surfaces.accent, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', boxShadow: c.myBubbleShadow,
          }}
        >
          <SquarePen size={19} />
        </button>
        )
      ) : (
        <div style={{ padding: '10px 16px', flexShrink: 0 }}>
          <button
            type="button"
            onClick={startChat}
            disabled={selectedIds.length === 0 || creatingGroup}
            style={{
              width: '100%', border: 'none', borderRadius: '12px',
              background: selectedIds.length === 0 || creatingGroup ? surfaces.inputBg : surfaces.accent,
              color: selectedIds.length === 0 || creatingGroup ? surfaces.muted : '#fff',
              fontWeight: 700, fontSize: '14px', padding: '11px',
              cursor: selectedIds.length === 0 || creatingGroup ? 'not-allowed' : 'pointer',
            }}
          >
            {creatingGroup ? '…' : (isRTL ? 'محادثة' : 'Chat')}
          </button>
        </div>
      )}
    </div>
  );

  // A focused conversation view: once a chat window is open (from either the
  // avatar stack or a panel row), hide the launcher pill entirely so only the
  // chat is visible — it reappears on its own once the window closes/is
  // minimized, since dockState itself never changes on open.
  const launcher = dockState === 'collapsed' && !hasActiveWindow ? collapsedPill : null;

  return (
    // `display: contents` — a real DOM node (so the outside-click ref can
    // attach to it) that otherwise behaves exactly like the Fragment this
    // used to be: no box, no layout footprint, doesn't affect the app shell.
    <div ref={containerRef} style={{ display: 'contents' }}>
      {/* Scoped scrollbar hiding for the mini chat windows only — NOT a
          global rule, so it can't affect scrollbars anywhere else in the app. */}
      <style>{`
        .mini-chat-scroll { scrollbar-width: none; -ms-overflow-style: none; }
        .mini-chat-scroll::-webkit-scrollbar { display: none; width: 0; height: 0; }
      `}</style>

      <div
        style={{
          position: 'fixed',
          bottom: '16px',
          [isRTL ? 'left' : 'right']: '16px',
          zIndex: 950,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          gap: '12px',
        }}
      >
        {isRTL && launcher}
        {panel}
        {!isRTL && launcher}
      </div>

      {openWindows.map((w, index) => (
        <ChatWindow key={w.chatId} chatId={w.chatId} minimized={w.minimized} index={index} />
      ))}
    </div>
  );
};

export default MiniChatDock;
